/**
 * PlatformBridgeAuth — Silent SSO from the unified PLATFORM (Slice 3).
 * Receives a one-time opaque bridge code via URL, exchanges it for a PRISM JWT through the backend
 * (server-to-server verification with the platform), then routes to the intended PRISM page.
 *
 * URL: /auth/platform-bridge?code=<opaqueCode>&redirect=<path>
 *
 * Security: the code is opaque (not a JWT), single-use, short-TTL. The auth payload never appears
 * in the URL or browser history. Sibling to BridgeAuth (the Foundations consumer); kept separate so
 * the platform issuer is decoupled from the Foundations flow.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { clearImpersonationStash } from '../services/impersonationSession';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export default function PlatformBridgeAuth() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const redirect = searchParams.get('redirect') || '/projects-v2';

    if (!code) {
      setError('No bridge code provided');
      return;
    }

    async function authenticate() {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/platform-bridge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          setError(data.error || 'Platform sign-in failed');
          return;
        }

        // A new sign-in owns this browser: a stash left by an unfinished view-as must never outlive it.
        clearImpersonationStash();
        localStorage.setItem('prism_token', data.data.token);
        if (data.data.user) {
          localStorage.setItem('prism_user', JSON.stringify(data.data.user));
        }

        navigate(data.data.redirectPath || redirect, { replace: true });
      } catch (err: any) {
        setError(err.message || 'Failed to connect to PRISM');
      }
    }

    authenticate();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2, p: 3 }}>
        <Alert role="alert" severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          This link may have expired. Please return to the platform and try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        Signing you in…
      </Typography>
    </Box>
  );
}
